/**
 * PlacementTestMetadataForm Component
 *
 * Displays and manages placement test-level metadata fields:
 * - Placement test name
 * - Placement test summary
 * - Placement test description
 * - Thumbnail URL
 * - Duration
 * - Start date
 * - Expiration date
 *
 * Note: Placement test media (asset) can now be updated as of npm-pikado-logic v1.1.44.
 */

import React from 'react';
import { Input, DatePicker, Typography } from 'antd';
import moment from 'moment';
import { CreateAssetBlock } from '../../../components';
import { Asset, AssetType } from '../../../graphql/API';
import { getPlacementTestStaticDescription } from '../../../utils/staticValuesUtils/placementTestStaticValues';

const { Text } = Typography;

export interface PlacementTestMetadataFormProps {
  placementTestName: string;
  onPlacementTestNameChange: (name: string) => void;
  placementTestDescription: string;
  onPlacementTestDescriptionChange: (description: string) => void;
  thumbnail: string;
  onThumbnailChange: (thumbnail: string) => void;
  media?: Array<{ type: string; url: string }>;
  placementTestAsset: Asset | undefined;
  onPlacementTestAssetChange: (asset: Asset) => void;
  duration: string;
  onDurationChange: (duration: string) => void;
  startDate: string;
  onStartDateChange: (startDate: string) => void;
  expirationDate: string;
  onExpirationDateChange: (expirationDate: string) => void;
}

/**
 * Form component for placement test-level metadata
 */
export const PlacementTestMetadataForm: React.FC<PlacementTestMetadataFormProps> = ({
  placementTestName,
  onPlacementTestNameChange,
  placementTestDescription,
  onPlacementTestDescriptionChange,
  thumbnail,
  onThumbnailChange,
  media,
  placementTestAsset,
  onPlacementTestAssetChange,
  duration,
  onDurationChange,
  startDate,
  onStartDateChange,
  expirationDate,
  onExpirationDateChange,
}) => {
  return (
    <div style={{ marginBottom: '30px' }}>
      {/* Placement Test Name */}
      <div style={{ marginTop: '20px', marginBottom: '20px' }}>
        <Text>Placement Test name</Text>
        <Input
          value={placementTestName}
          onChange={(e) => onPlacementTestNameChange(e.target.value)}
          placeholder="Placement Test name"
        />
      </div>

      {/* Thumbnail URL */}
      {/* <div style={{ marginTop: '20px', marginBottom: '20px' }}>
        <Text>Thumbnail URL (optional)</Text>
        <Input
          value={thumbnail}
          onChange={(e) => onThumbnailChange(e.target.value)}
          placeholder="Enter thumbnail image URL"
        />
        <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginTop: '5px' }}>
          Thumbnail will be automatically extracted from the media asset.
        </Text>
      </div> */}

      {/* Placement Test Asset (Video/Photo/Audio) - EDITABLE */}
      <div style={{ marginTop: '20px', marginBottom: '20px' }}>
        <CreateAssetBlock
          asset={placementTestAsset}
          defaultAssetType={placementTestAsset?.type ?? AssetType.VIDEO}
          onChange={(selectedAsset) => onPlacementTestAssetChange(selectedAsset)}
        />
      </div>

      {/* Duration */}
      <div style={{ marginTop: '20px', marginBottom: '20px' }}>
        <Text>Duration (e.g., "30 minutes", "1 hour")</Text>
        <Input
          value={duration}
          onChange={(e) => onDurationChange(e.target.value)}
          placeholder="e.g., 30 minutes"
        />
      </div>

      {/* Start Date */}
      <div style={{ marginTop: '20px', marginBottom: '20px' }}>
        <Text style={{ display: 'block' }}>Start Date</Text>
        <DatePicker
          style={{ width: '100%' }}
          showTime
          value={startDate ? moment(startDate) : null}
          onChange={(date) => {
            onStartDateChange(date ? date.toISOString() : '');
          }}
          placeholder="Select start date"
        />
      </div>

      {/* Expiration Date */}
      <div style={{ marginTop: '20px', marginBottom: '20px' }}>
        <Text style={{ display: 'block' }}>Expiration Date</Text>
        <DatePicker
          style={{ width: '100%' }}
          showTime
          value={expirationDate ? moment(expirationDate) : null}
          onChange={(date) => {
            onExpirationDateChange(date ? date.toISOString() : '');
          }}
          placeholder="Select expiration date"
        />
      </div>

      {/* Display media array */}
      {media && media.length > 0 && (
        <div style={{ marginTop: '20px', padding: '10px', border: '1px solid #d9d9d9', backgroundColor: '#f5f5f5' }}>
          <Text strong>Current Media (read-only reference):</Text>
          {media.map((mediaItem: any, index: number) => (
            <div key={index} style={{ marginTop: '10px' }}>
              <Text>Media {index + 1} - Type: {mediaItem.type}</Text>
              <br />
              <Text type="secondary" style={{ fontSize: '12px', wordBreak: 'break-all' }}>
                {mediaItem.url}
              </Text>
            </div>
          ))}
        </div>
      )}

      {/* Placement Test Description */}
      <div style={{ marginTop: '20px', marginBottom: '20px' }}>
        <Text>Placement Test description</Text>
        <Input
          value={getPlacementTestStaticDescription(placementTestAsset?.type || 'DEFAULT')}
          onChange={(e) => {}}
          placeholder="Placement Test description"
          disabled
        />
      </div>
    </div>
  );
};
